import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './tufts-yext-location.css.lit';
import type { YextLocation } from '../tufts-yext/tufts-yext-types';
import '../tufts-other-location-listing/tufts-other-location-listing';
import '../tufts-leaflet-map/tufts-leaflet-map';
import '../tufts-other-location-result/tufts-other-location-result';
/* eslint-disable lit/attribute-value-entities */

@customElement('tufts-yext-location')
export class TuftsYextLocation extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  // Keep the external property as string for compatibility
  private _locations = '[]';

  @property({ type: String, reflect: true, attribute: 'locations' })
  get locations() {
    return this._locations;
  }

  set locations(value: string) {
    const oldValue = this._locations;
    this._locations = value;

    try {
      // Update the locations array when the property changes
      this.locationsArray = JSON.parse(value) as YextLocation[];

      // Load the location URLs
      this.loadLocationUrls();
    } catch (error) {
      console.error('Error parsing locations:', error);
    }

    this.requestUpdate('locations', oldValue);
  }

  @state() locationsArray: YextLocation[] = [];

  // Store the URLs of Drupal nodes for each location
  @state() locationUrls: Record<string, string> = {};

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    // No need to duplicate work here, as the setter already handles initialization
  }

  // Method to load location URLs from Drupal API
  async loadLocationUrls() {
    if (this.locationsArray && this.locationsArray.length > 0) {
      try {
        // Fetch all location URLs in parallel
        const pairs = await Promise.all(
          this.locationsArray.map(async loc => {
            if (!loc.title) return undefined;

            try {
              const r = await fetch(
                `/tufts-api/location-by-title/${encodeURIComponent(loc.title)}`
              );
              if (!r.ok) return undefined;

              const d = await r.json();
              return d.found ? [loc.title, d.url] : undefined;
            } catch {
              // Swallow the error silently
              return undefined;
            }
          })
        );

        // Filter out undefined values and convert to an object
        this.locationUrls = Object.fromEntries(
          pairs.filter(Boolean) as [string, string][]
        );
        this.requestUpdate();
      } catch (error) {
        console.error('Error loading location URLs:', error);
      }
    }
  }

  // Method to retrieve the Drupal URL for a specific title
  getLocationUrl(title: string): string | undefined {
    return this.locationUrls[title];
  }

  render(): TemplateResult {
    return html`
      <tufts-other-location-listing vertical-trigger-width="1080">
        <div slot="icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M31.8408 63.29C31.1308 63.29 30.4108 63.28 29.7208 63.25C27.5608 63.16 16.8008 62.52 16.8008 58.35C16.8008 55.73 21.0608 54.51 24.6408 53.94L25.0208 56.31C20.7308 56.99 19.4008 58.04 19.2108 58.35C19.5808 59.09 23.0608 60.58 29.8208 60.85C31.1508 60.91 32.5308 60.91 33.8608 60.85C40.6208 60.58 44.1008 59.09 44.4708 58.35C44.2908 58.04 42.9608 56.99 38.6708 56.31L39.0508 53.94C42.6208 54.51 46.8808 55.73 46.8808 58.35C46.8808 62.51 36.1208 63.16 33.9608 63.25C33.2608 63.28 32.5708 63.29 31.8508 63.29H31.8408Z"
              fill="#428FEC"
            />
            <path
              d="M45.5014 22.84C45.5014 30.39 39.3914 36.5 31.8514 36.5C24.3114 36.5 18.1914 30.4 18.1914 22.84C18.1914 15.28 24.3114 9.17999 31.8514 9.17999C39.3914 9.17999 45.5014 15.3 45.5014 22.84Z"
              fill="#428FEC"
            />
            <path
              d="M31.84 59.55C31.45 59.55 31.09 59.36 30.86 59.05L14.5 36.12C11.51 31.93 10 27.46 10 22.84C10 10.8 19.8 1 31.84 1C43.88 1 53.68 10.8 53.68 22.84C53.68 27.4 52.13 31.99 49.18 36.11L32.81 59.05C32.58 59.37 32.22 59.55 31.83 59.55H31.84ZM31.84 3.39C21.12 3.4 12.4 12.12 12.4 22.84C12.4 26.95 13.76 30.95 16.45 34.73L31.84 56.3L47.23 34.73C49.92 30.96 51.28 26.96 51.28 22.85C51.28 12.13 42.56 3.41 31.84 3.41V3.39Z"
              fill="#00308C"
            />
            <path
              d="M31.8391 37.63C23.6791 37.63 17.0391 30.99 17.0391 22.83C17.0391 14.67 23.6791 8.03998 31.8391 8.03998C39.9991 8.03998 46.6291 14.68 46.6291 22.83C46.6291 30.98 39.9891 37.63 31.8391 37.63V37.63ZM31.8391 10.32C24.9391 10.32 19.3191 15.94 19.3191 22.84C19.3191 29.74 24.9391 35.36 31.8391 35.36C38.7391 35.36 44.3591 29.74 44.3591 22.84C44.3591 15.94 38.7391 10.32 31.8391 10.32V10.32Z"
              fill="#00308C"
            />
            <path
              d="M33.0406 16.85H30.6406V28.83H33.0406V16.85Z"
              fill="white"
            />
            <path
              d="M37.8316 21.64H25.8516V24.04H37.8316V21.64Z"
              fill="white"
            />
          </svg>
        </div>
        <div slot="title">Locations</div>
        ${this.locationsArray && this.locationsArray.length > 0
          ? html` <div slot="map">
              <tufts-leaflet-map
                markers=${JSON.stringify(
                  this.locationsArray.map(location => {
                    return {
                      lat: location.latitude,
                      lng: location.longitude,
                      title: location.title,
                    };
                  })
                )}
              ></tufts-leaflet-map>
            </div>`
          : null}
        ${this.locationsArray && this.locationsArray.length > 0
          ? this.locationsArray.map(location => {
              const compiledAddress = `${location.address} ${
                location.address_2 ? location.address_2 : ''
              } ${location.address_3 ? location.address_3 : ''}`;
              return html`
                <div slot="location-info">
                  <tufts-other-location-result>
                    <div slot="location-name">${location.title}</div>
                    <div slot="address">
                      <div class="multi-line">
                        <span>${location.address}</span>
                        ${location.address_2
                          ? html`<span>${location.address_2}</span>`
                          : null}
                        ${location.address_3
                          ? html`<span>${location.address_3}</span>`
                          : null}
                      </div>
                    </div>
                    ${location.number
                      ? html`
                          <div slot="address">
                            <div class="number-container">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M18.9922 11.995C18.9922 10.0461 18.3126 8.397 16.9533 7.03776C15.5941 5.67851 13.945 4.99889 11.9961 4.99889V3C13.2454 3 14.4148 3.23987 15.5041 3.70961C16.5935 4.18934 17.543 4.82898 18.3526 5.63853C19.1621 6.44808 19.8018 7.39756 20.2815 8.48695C20.7512 9.57635 20.9911 10.7457 20.9911 11.995H18.9922Z"
                                  fill="#428FEC"
                                ></path>
                                <path
                                  d="M14.9944 11.995C14.9944 11.1655 14.7046 10.4559 14.1149 9.8762C13.5252 9.29653 12.8256 8.99669 11.9961 8.99669V6.9978C13.3753 6.9978 14.5547 7.48753 15.5341 8.45699C16.5036 9.43645 16.9933 10.6058 16.9933 11.995H14.9944Z"
                                  fill="#428FEC"
                                ></path>
                                <path
                                  d="M20.7701 15.2832C20.6202 15.1133 20.4303 14.9933 20.1905 14.9434L16.7424 14.2438C16.5125 14.2138 16.2726 14.2338 16.0328 14.3037C15.7929 14.3837 15.593 14.4936 15.4431 14.6435L13.0944 16.9922C11.8251 16.2227 10.6657 15.3232 9.60633 14.2737C8.54692 13.2243 7.66741 12.0949 6.9678 10.8956L9.39645 8.44697C9.54636 8.29706 9.64631 8.12715 9.68629 7.93726C9.72626 7.74736 9.73626 7.53748 9.69628 7.29761L9.04664 3.79956C9.01666 3.56968 8.90672 3.37979 8.71682 3.21988C8.53692 3.06996 8.32704 3 8.09717 3H4.04942C3.74958 3 3.49972 3.09994 3.29983 3.29983C3.09994 3.49972 3 3.74958 3 4.04942C3 6.19822 3.47973 8.29706 4.4392 10.3359C5.39867 12.3748 6.65797 14.1838 8.23709 15.7629C9.80622 17.342 11.6152 18.6013 13.6641 19.5608C15.7029 20.5203 17.8018 21 19.9506 21C20.2504 21 20.5003 20.9001 20.7002 20.7002C20.9001 20.5003 21 20.2504 21 19.9506V15.9028C21 15.673 20.92 15.4631 20.7801 15.2932L20.7701 15.2832ZM5.37868 7.02776C5.22876 6.35813 5.10883 5.68851 5.02887 4.99889H7.24764L7.66741 7.34758L6.01832 8.99667C5.73848 8.34703 5.5186 7.6874 5.36868 7.02776H5.37868ZM18.9911 18.9411C18.3115 18.8912 17.6319 18.7812 16.9522 18.6113C16.2726 18.4414 15.613 18.2215 14.9634 17.9417L16.6424 16.2726L18.9911 16.7424V18.9411Z"
                                  fill="#00308C"
                                ></path>
                              </svg>
                              <span class="visually-hidden">Phone Number</span>
                              <a href="tel:${location.number}">
                                ${location.number}
                              </a>
                            </div>
                          </div>
                        `
                      : null}

                    <div slot="additional-information">
                      ${this.getLocationUrl(location.title)
                        ? html`<span class="icon-information">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 12C12.55 12 13.02 11.8 13.41 11.41C13.8 11.02 14 10.55 14 10C14 9.45 13.8 8.98 13.41 8.59C13.02 8.2 12.55 8 12 8C11.45 8 10.98 8.2 10.59 8.59C10.2 8.98 10 9.45 10 10C10 10.55 10.2 11.02 10.59 11.41C10.98 11.8 11.45 12 12 12ZM12 12C12.55 12 13.02 11.8 13.41 11.41C13.8 11.02 14 10.55 14 10C14 9.45 13.8 8.98 13.41 8.59C13.02 8.2 12.55 8 12 8C11.45 8 10.98 8.2 10.59 8.59C10.2 8.98 10 9.45 10 10C10 10.55 10.2 11.02 10.59 11.41C10.98 11.8 11.45 12 12 12ZM12 12C12.55 12 13.02 11.8 13.41 11.41C13.8 11.02 14 10.55 14 10C14 9.45 13.8 8.98 13.41 8.59C13.02 8.2 12.55 8 12 8C11.45 8 10.98 8.2 10.59 8.59C10.2 8.98 10 9.45 10 10C10 10.55 10.2 11.02 10.59 11.41C10.98 11.8 11.45 12 12 12Z"
                                fill="#428FEC"
                              />
                              <path
                                d="M12.0018 22.06L10.6518 20.82C8.47181 18.82 6.88181 17.03 5.80181 15.34C4.59181 13.47 4.01181 11.78 4.01181 10.19C4.00181 7.84 4.78181 5.86 6.32181 4.32C7.86181 2.78 9.77181 2 12.0018 2C14.2318 2 16.1318 2.78 17.6818 4.32C19.2218 5.86 20.0018 7.84 20.0018 10.2C20.0018 11.79 19.4118 13.47 18.2118 15.35C17.1218 17.04 15.5418 18.83 13.3618 20.83L12.0118 22.07L12.0018 22.06ZM12.0018 19.35C14.0218 17.49 15.5518 15.78 16.5218 14.26C17.5018 12.74 18.0018 11.38 18.0018 10.2C18.0018 8.39 17.4218 6.89 16.2618 5.74C15.1018 4.59 13.6718 4 12.0018 4C10.3318 4 8.89181 4.58 7.74181 5.74C6.59181 6.89 6.00181 8.39 6.00181 10.2C6.00181 11.38 6.50181 12.74 7.48181 14.26C8.46181 15.78 9.98181 17.49 12.0018 19.35Z"
                                fill="#00308C"
                              />
                            </svg>
                            <a href="${this.getLocationUrl(location.title)}"
                              >Location details</a
                            >
                          </span>`
                        : null}
                      <span class="icon-information">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15 21L9 18.9L4.35 20.7C4.01667 20.8333 3.70833 20.7957 3.425 20.587C3.14167 20.379 3 20.1 3 19.75V5.75C3 5.53333 3.06267 5.34167 3.188 5.175C3.31267 5.00833 3.48333 4.88333 3.7 4.8L9 3L15 5.1L19.65 3.3C19.9833 3.16667 20.2917 3.204 20.575 3.412C20.8583 3.62067 21 3.9 21 4.25V18.25C21 18.4667 20.9373 18.6583 20.812 18.825C20.6873 18.9917 20.5167 19.1167 20.3 19.2L15 21ZM14 18.55V6.85L10 5.45V17.15L14 18.55ZM16 18.55L19 17.55V5.7L16 6.85V18.55ZM5 18.3L8 17.15V5.45L5 6.45V18.3Z"
                            fill="#00308C"
                          />
                        </svg>
                        <a
                          href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                            compiledAddress
                          )}"
                          target="_blank"
                          rel="noopener noreferrer"
                          >Get directions</a
                        >
                      </span>
                    </div>
                  </tufts-other-location-result>
                </div>
              `;
            })
          : null}
      </tufts-other-location-listing>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-yext-location': TuftsYextLocation;
  }
}
